import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom'; // Añade useLocation
import { Menu, X, ChevronDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from './LanguajeContext';

export default function Navbar() {
  const { language, setLanguage } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [languageDropdownOpen, setLanguageDropdownOpen] = useState(false);
  const location = useLocation(); // Obtiene la ubicación actual

  const languages = [
    { code: 'es', name: 'Español' },
    { code: 'en', name: 'English' }
  ];

  // Determina si estamos en una ruta donde queremos ocultar el selector de idioma
  const shouldHideLanguageSelector = ['/registro', '/login'].includes(location.pathname);

  const handleLanguageChange = (lang) => {
    setLanguage(lang);
    setLanguageDropdownOpen(false);
  };

  return (
    <>
      <nav className="bg-white text-[#0F4C5C] shadow-md fixed w-full z-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="text-2xl font-bold flex items-center gap-2">
              <span className="text-[#66B032]">Pia</span>
              <span className="text-[#0F4C5C]">Wak</span>
            </Link>

            {/* Desktop links */}
            <div className="flex items-center space-x-6 hidden md:flex font-medium text-sm">
              <Link to="/" className="hover:text-[#66B032] transition duration-200">
                {language === 'es' ? 'Inicio' : 'Home'}
              </Link>
              <Link to="/registro" className="hover:text-[#66B032] transition duration-200">
                {language === 'es' ? 'Registro' : 'Register'}
              </Link>
              <Link to="/login" className="hover:text-[#66B032] transition duration-200">
                {language === 'es' ? 'Login' : 'Login'}
              </Link>
              
              {/* Language Selector Desktop - Solo se muestra si no estamos en registro/login */}
              {!shouldHideLanguageSelector && (
                <div className="relative">
                  <button 
                    onClick={() => setLanguageDropdownOpen(!languageDropdownOpen)}
                    className="flex items-center gap-1 hover:text-[#66B032] transition"
                  >
                    {language === 'es' ? 'ES' : 'EN'}
                    <ChevronDown className={`w-4 h-4 transition-transform ${languageDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  <AnimatePresence>
                    {languageDropdownOpen && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.2 }}
                        className="absolute right-0 mt-2 w-32 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-100"
                      >
                        {languages.map((lang) => (
                          <button
                            key={lang.code}
                            onClick={() => handleLanguageChange(lang.code)}
                            className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${language === lang.code ? 'text-[#66B032] font-medium' : 'text-gray-700'}`}
                          >
                            {lang.name}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden flex items-center gap-4">
              {/* Solo muestra el botón de idioma si no estamos en registro/login */}
              {!shouldHideLanguageSelector && (
                <button 
                  onClick={() => setLanguage(language === 'es' ? 'en' : 'es')}
                  className="text-sm font-medium px-2 py-1 rounded border border-gray-200"
                >
                  {language === 'es' ? 'EN' : 'ES'}
                </button>
              )}
              
              <button onClick={() => setIsOpen(true)}>
                <Menu className="w-6 h-6 text-[#0F4C5C]" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile slide-in menu with overlay */}
      <AnimatePresence>
        {isOpen && (
          <>
            {/* Overlay */}
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-40 z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsOpen(false)}
            />

            {/* Menu */}
            <motion.div
              initial={{ x: '100%' }}
              animate={{ x: 0 }}
              exit={{ x: '100%' }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              className="fixed top-0 right-0 w-3/4 max-w-xs h-full bg-white shadow-lg z-50 p-6 md:hidden"
            >
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-[#0F4C5C]">
                  {language === 'es' ? 'Menú' : 'Menu'}
                </h2>
                <button onClick={() => setIsOpen(false)}>
                  <X className="w-6 h-6 text-gray-600" />
                </button>
              </div>
              
              <div className="flex flex-col space-y-4 font-medium text-[#0F4C5C]">
                <Link to="/" onClick={() => setIsOpen(false)} className="hover:text-[#66B032] transition">
                  {language === 'es' ? 'Inicio' : 'Home'}
                </Link>
                <Link to="/registro" onClick={() => setIsOpen(false)} className="hover:text-[#66B032] transition">
                  {language === 'es' ? 'Registro' : 'Register'}
                </Link>
                <Link to="/login" onClick={() => setIsOpen(false)} className="hover:text-[#66B032] transition">
                  {language === 'es' ? 'Login' : 'Login'}
                </Link>
                
                {/* Language selector in mobile menu - Solo se muestra si no estamos en registro/login */}
                {!shouldHideLanguageSelector && (
                  <div className="pt-4 border-t border-gray-100 mt-4">
                    <p className="text-sm text-gray-500 mb-2">
                      {language === 'es' ? 'Idioma' : 'Language'}
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          setLanguage('es');
                          setIsOpen(false);
                        }}
                        className={`px-3 py-1 text-sm rounded ${language === 'es' ? 'bg-[#66B032] text-white' : 'bg-gray-100 text-gray-700'}`}
                      >
                        Español
                      </button>
                      <button
                        onClick={() => {
                          setLanguage('en');
                          setIsOpen(false);
                        }}
                        className={`px-3 py-1 text-sm rounded ${language === 'en' ? 'bg-[#66B032] text-white' : 'bg-gray-100 text-gray-700'}`}
                      >
                        English
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
}